import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Download, Copy, CheckCircle2, Play, Pause, ArrowLeft, Film, Sparkles,
  Linkedin, Twitter, Facebook, Link as LinkIcon, Code2,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

/**
 * Public marketing-kit download hub. Lives at /marketing-kit.
 *
 * Surfaces every video + social-caption snippet we've generated so the
 * founder, marketing contractors, a VA, or a PR partner can grab approved
 * brand assets in 30 seconds without bothering engineering.
 */

const ASSETS = [
  {
    id: "narrated_9x16",
    title: "Narrated pitch · Vertical",
    tagline: "30 seconds · onyx voiceover · 720×1280",
    description:
      "Our flagship investor pitch video. 30-second onyx narration + 5 on-screen messaging beats + branded hold card. Built for Instagram Reels, TikTok, LinkedIn mobile, YouTube Shorts.",
    video: "/marketing/narrated_9x16.mp4",
    poster: "/marketing/narrated_9x16.jpg",
    filename: "realtyvault-narrated-30s-vertical.mp4",
    aspect: "9:16",
    duration: "30s",
    size: "4.0 MB",
    withAudio: true,
    recommended: ["Instagram Reels", "TikTok", "YouTube Shorts", "LinkedIn mobile"],
    caption:
      "Every quarter your lender asks for the same covenant package.\n\nMarket values. Loan balances. Cap rates. Rent roll. DSCR.\n\nWe built the one-click branded Schedule of Real Estate Owned so you never spend another weekend in spreadsheets.\n\n#CommercialRealEstate #CRE #PropTech #LenderReporting",
  },
  {
    id: "narrated",
    title: "Narrated pitch · Landscape",
    tagline: "30 seconds · onyx voiceover · 1280×720",
    description:
      "Same 30-second investor narration, 16:9 cinematic crop. Ideal for LinkedIn native video, email campaigns, YouTube, and the landing page.",
    video: "/marketing/narrated.mp4",
    poster: "/marketing/narrated.jpg",
    filename: "realtyvault-narrated-30s-landscape.mp4",
    aspect: "16:9",
    duration: "30s",
    size: "4.3 MB",
    withAudio: true,
    recommended: ["LinkedIn native", "YouTube", "Email embed", "Landing hero"],
    caption:
      "Your lender asks for the same package every quarter. We built it in one click.\n\nWatch the 30-second pitch ↓\n\n#CommercialRealEstate #CRE #PropTech #LenderReporting #SREO",
  },
  {
    id: "feature_9x16",
    title: "Feature showcase · Vertical",
    tagline: "12 seconds · silent + captions · 720×1280",
    description:
      "Punchy 12-second product-focused teaser with 5 on-screen captions. No voiceover — designed for autoplay-muted social feeds.",
    video: "/marketing/feature_9x16.mp4",
    poster: "/marketing/feature_9x16.jpg",
    filename: "realtyvault-feature-12s-vertical.mp4",
    aspect: "9:16",
    duration: "12s",
    size: "1.9 MB",
    withAudio: false,
    recommended: ["Instagram Feed", "TikTok", "Reels", "Pinterest"],
    caption:
      "Your portfolio. One screen. Live cap rates. SREO reports in one click. Email your lender the branded PDF.\n\nRealtyVault Manager — built for commercial investors.\n\n#CRE #CommercialRealEstate #PropTech",
  },
  {
    id: "feature",
    title: "Feature showcase · Landscape",
    tagline: "12 seconds · silent + captions · 1280×720",
    description:
      "Landscape twin of the feature showcase. Captions sized for desktop feeds — perfect for LinkedIn native video and Twitter/X uploads.",
    video: "/marketing/feature.mp4",
    poster: "/marketing/feature.jpg",
    filename: "realtyvault-feature-12s-landscape.mp4",
    aspect: "16:9",
    duration: "12s",
    size: "2.2 MB",
    withAudio: false,
    recommended: ["LinkedIn", "X / Twitter", "Email embed", "Facebook"],
    caption:
      "Your commercial portfolio on one screen. SREO + rent roll + cap rates + lender emails in one click.\n\nRealtyVault Manager.\n\n#CRE #CommercialRealEstate #PropTech #LenderReporting",
  },
  {
    id: "hero",
    title: "Hero beauty shot · Landscape",
    tagline: "12 seconds · silent · 1280×720",
    description:
      "Pure cinematic CRE beauty footage — aerial plaza at dusk, modern lobby, golden-hour facade. The premium ambient loop we embed on the landing page.",
    video: "/marketing/hero.mp4",
    poster: "/marketing/hero.jpg",
    filename: "realtyvault-hero-12s-landscape.mp4",
    aspect: "16:9",
    duration: "12s",
    size: "7.8 MB",
    withAudio: false,
    recommended: ["Landing page", "Trade show booth", "Background ambient"],
    caption:
      "Commercial real estate, managed like software.\n\nRealtyVault Manager.",
  },
  {
    id: "social",
    title: "Ambient beauty · Vertical",
    tagline: "12 seconds · silent · 720×1280",
    description:
      "Generic cinematic CRE vertical — derived from the hero shoot. Use when you want atmosphere without a specific message, or as b-roll for your own edits.",
    video: "/marketing/social.mp4",
    poster: "/marketing/social.jpg",
    filename: "realtyvault-ambient-12s-vertical.mp4",
    aspect: "9:16",
    duration: "12s",
    size: "2.0 MB",
    withAudio: false,
    recommended: ["B-roll", "Transition filler", "Story backgrounds"],
    caption:
      "Commercial real estate, managed like software.\n\n#CRE #CommercialRealEstate",
  },
];

export default function MarketingKit() {
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success("Caption copied");
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="marketing-kit-page">
      <SEO
        title="Marketing Kit — Videos, Social Captions & Brand Assets"
        description="Download narrated investor pitch videos (9:16 + 16:9), feature-showcase clips, and pre-written social captions for RealtyVault Manager. No watermarks."
        path="/marketing-kit"
      />
      {/* Top nav */}
      <header className="border-b border-white/10 bg-slate-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2"
            data-testid="marketing-kit-back"
          >
            <ArrowLeft className="w-4 h-4" /> RealtyVault Manager
          </Link>
          <div className="overline text-amber-300/80 hidden md:block">Marketing Kit</div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative px-6 md:px-10 py-20 max-w-7xl mx-auto">
        <div className="absolute inset-x-0 top-0 h-80 bg-[radial-gradient(ellipse_at_top,rgba(251,191,36,0.12),transparent_60%)] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 overline text-amber-300/80 mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Brand assets · Approved · Shareable
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter"
            style={{ fontFamily: "Outfit" }}
          >
            The RealtyVault Manager <span className="text-white/40 italic font-light">marketing kit.</span>
          </h1>
          <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
            Every production video, poster frame, and social caption we've shipped — in one place.
            Grab what you need, hit post. No login, no approvals, no asking engineering.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-white/50">
            <Stat label={`${ASSETS.length} videos`} />
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <Stat label="Vertical + landscape" />
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <Stat label="H.264 + AAC" />
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <Stat label="Social-ready, no watermark" />
          </div>
        </div>
      </section>

      {/* Asset grid */}
      <section className="px-6 md:px-10 pb-32 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {ASSETS.map((a) => (
            <AssetCard
              key={a.id}
              asset={a}
              copiedId={copiedId}
              onCopy={copy}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-20 border-t border-white/10 pt-10">
          {/* QA row — validator deep-links */}
          <div className="mb-10">
            <div className="overline text-amber-300/80 mb-3">Before you post · Test your share preview</div>
            <p className="text-sm text-white/60 leading-relaxed mb-4 max-w-2xl">
              Open the official debuggers to confirm your link unfurls with the branded poster + title + description.
              LinkedIn &amp; Facebook aggressively cache — use these tools once you've updated any meta tags to force a refresh.
            </p>
            <div className="flex flex-wrap gap-2">
              <ValidatorBtn
                label="LinkedIn Post Inspector"
                icon={<Linkedin className="w-3.5 h-3.5" />}
                testid="validator-linkedin"
                build={(u) => `https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(u)}`}
                platform="linkedin"
              />
              <ValidatorBtn
                label="Facebook Sharing Debugger"
                icon={<Facebook className="w-3.5 h-3.5" />}
                testid="validator-facebook"
                build={(u) => `https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(u)}`}
                platform="facebook"
              />
              <ValidatorBtn
                label="OpenGraph preview"
                icon={<Sparkles className="w-3.5 h-3.5" />}
                testid="validator-opengraph"
                build={(u) => `https://www.opengraph.xyz/url/${encodeURIComponent(u)}`}
                platform="x"
              />
            </div>
            <p className="mt-3 text-[11px] text-white/30 leading-snug">
              Note: X &amp; Twitter's official Card Validator requires login — use the generic OpenGraph previewer (above) for a free, no-login render that mirrors what X will show.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <div className="overline text-amber-300/80 mb-3">Usage guidelines</div>
              <ul className="text-sm text-white/60 space-y-2 leading-relaxed">
                <li>• Videos are no-watermark and cleared for paid or organic social.</li>
                <li>• Captions are starting points — feel free to remix for your audience.</li>
                <li>• For new video requests (different script, fresh footage, different voice), re-run the generation scripts under <code className="text-amber-300/70 font-mono text-xs">/app/scripts/</code>.</li>
                <li>• The "narrated" variants carry an onyx OpenAI TTS voiceover. Keep the master MP3 at <code className="text-amber-300/70 font-mono text-xs">/marketing/narration.mp3</code> if you want to remix.</li>
              </ul>
            </div>
            <div>
              <div className="overline text-amber-300/80 mb-3">Need a refresh?</div>
              <p className="text-sm text-white/60 leading-relaxed">
                Two scripts regenerate everything:
              </p>
              <div className="mt-3 space-y-2 font-mono text-xs text-white/70">
                <code className="block bg-white/5 border border-white/10 rounded px-3 py-2">
                  python /app/scripts/generate_feature_video.py
                </code>
                <code className="block bg-white/5 border border-white/10 rounded px-3 py-2">
                  python /app/scripts/generate_narrated_video.py
                </code>
              </div>
              <p className="mt-3 text-xs text-white/40">
                Set <span className="font-mono text-amber-300/70">REUSE_RAW=1</span> or{" "}
                <span className="font-mono text-amber-300/70">REUSE_NARRATION=1</span> to tweak captions without burning another Sora/TTS credit.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label }) {
  return <span className="uppercase tracking-widest">{label}</span>;
}

function ShareBtn({ platform, label, icon, onClick, dataTestid }) {
  // Platform-specific brand colors on hover for subtle visual affordance.
  const hoverBg =
    platform === "linkedin"
      ? "hover:bg-[#0A66C2]/20 hover:border-[#0A66C2]/50 hover:text-[#70B5F9]"
      : platform === "x"
      ? "hover:bg-white/15 hover:border-white/30 hover:text-white"
      : "hover:bg-[#1877F2]/20 hover:border-[#1877F2]/50 hover:text-[#8BB9F7]";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={dataTestid}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/70 transition-colors ${hoverBg}`}
    >
      {icon} {label}
    </button>
  );
}

function ValidatorBtn({ label, icon, build, platform, testid }) {
  const hover =
    platform === "linkedin"
      ? "hover:bg-[#0A66C2]/20 hover:border-[#0A66C2]/50 hover:text-[#70B5F9]"
      : platform === "facebook"
      ? "hover:bg-[#1877F2]/20 hover:border-[#1877F2]/50 hover:text-[#8BB9F7]"
      : "hover:bg-amber-300/15 hover:border-amber-300/40 hover:text-amber-200";
  const open = () => {
    const siteUrl = typeof window !== "undefined" ? window.location.origin + "/" : "/";
    window.open(build(siteUrl), "_blank", "noopener,noreferrer");
  };
  return (
    <button
      type="button"
      onClick={open}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white/70 transition-colors ${hover}`}
    >
      {icon} {label}
    </button>
  );
}

function AssetCard({ asset, copiedId, onCopy }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  // Build the absolute video URL that social platforms will share.
  // Use window.location.origin so preview & prod both resolve correctly.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const absoluteVideoUrl = `${origin}${asset.video}`;
  const shareUrl = `${origin}/marketing-kit`;

  const share = (platform) => {
    const text = encodeURIComponent(asset.caption);
    const url = encodeURIComponent(platform === "x" ? absoluteVideoUrl : shareUrl);
    let target = "";
    if (platform === "linkedin") {
      // LinkedIn's share-offsite compositor accepts url + auto-detects OG data.
      // We share the marketing-kit page (not the raw mp4) so the click-through
      // lands somewhere readable.
      target = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    } else if (platform === "x") {
      // X/Twitter accepts both text + url — mp4 URLs sometimes auto-embed,
      // otherwise render as a link card.
      target = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    } else if (platform === "facebook") {
      // FB's u= param is the canonical share target.
      target = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    }
    if (target) {
      window.open(target, "_blank", "noopener,noreferrer,width=640,height=720");
    }
  };

  const copyVideoUrl = async () => {
    try {
      await navigator.clipboard.writeText(absoluteVideoUrl);
      setUrlCopied(true);
      toast.success("Video URL copied");
      setTimeout(() => setUrlCopied(false), 1800);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  // Build a drop-in HTML <video> snippet. Autoplay-muted-loop for silent
  // variants (Notion/email signatures/Webflow friendly); controlled playback
  // for narrated variants so the voiceover isn't lost to autoplay-sound
  // blocking.
  const embedSnippet = (() => {
    const posterUrl = `${origin}${asset.poster}`;
    const widthCap = asset.aspect === "9:16" ? 320 : 640;
    const attrs = asset.withAudio
      ? 'controls playsinline preload="metadata"'
      : "autoplay muted loop playsinline";
    return `<video
  src="${absoluteVideoUrl}"
  poster="${posterUrl}"
  ${attrs}
  style="width:100%;max-width:${widthCap}px;border-radius:12px;display:block;"
></video>`;
  })();

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setEmbedCopied(true);
      toast.success("Embed snippet copied", {
        description: "Paste into Notion, Webflow, email signatures, help docs.",
      });
      setTimeout(() => setEmbedCopied(false), 1800);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <article
      className="glass rounded-2xl border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
      data-testid={`asset-card-${asset.id}`}
    >
      {/* Video preview */}
      <div
        className={`relative bg-black ${
          asset.aspect === "9:16" ? "aspect-[9/16] max-w-xs mx-auto" : "aspect-video"
        }`}
      >
        <video
          ref={videoRef}
          src={asset.video}
          poster={asset.poster}
          muted={!asset.withAudio}
          playsInline
          loop
          className="w-full h-full object-cover"
          onEnded={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          data-testid={`asset-video-${asset.id}`}
        />
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center group bg-black/0 hover:bg-black/30 transition-colors"
          data-testid={`asset-play-${asset.id}`}
          aria-label={playing ? "Pause preview" : "Play preview"}
        >
          <span
            className={`w-14 h-14 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg transition-transform group-hover:scale-105 ${
              playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
            }`}
          >
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </span>
        </button>
        {/* Aspect ratio + duration chip */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className="px-2 py-1 rounded-full bg-black/70 backdrop-blur text-[10px] font-bold tracking-wider text-white">
            {asset.aspect}
          </span>
          <span className="px-2 py-1 rounded-full bg-black/70 backdrop-blur text-[10px] font-bold tracking-wider text-white">
            {asset.duration}
          </span>
          {asset.withAudio && (
            <span className="px-2 py-1 rounded-full bg-amber-500/90 text-[10px] font-bold tracking-wider text-slate-900">
              Voiceover
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-6 space-y-4">
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2
                className="text-xl font-bold tracking-tight"
                style={{ fontFamily: "Outfit" }}
              >
                {asset.title}
              </h2>
              <div className="overline text-white/40 mt-1">{asset.tagline}</div>
            </div>
            <Film className="w-5 h-5 text-white/30 shrink-0" />
          </div>
          <p className="text-sm text-white/60 leading-relaxed mt-3">{asset.description}</p>
        </div>

        {/* Recommended uses */}
        <div>
          <div className="overline text-white/40 mb-2">Best for</div>
          <div className="flex flex-wrap gap-1.5">
            {asset.recommended.map((r) => (
              <span
                key={r}
                className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70"
              >
                {r}
              </span>
            ))}
          </div>
        </div>

        {/* Suggested caption */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-white/40">Suggested caption</div>
            <button
              type="button"
              onClick={() => onCopy(asset.caption, asset.id)}
              className="inline-flex items-center gap-1.5 text-xs text-amber-300/80 hover:text-amber-200 transition-colors"
              data-testid={`asset-copy-${asset.id}`}
            >
              {copiedId === asset.id ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> Copy
                </>
              )}
            </button>
          </div>
          <pre
            className="text-xs text-white/80 whitespace-pre-wrap bg-white/5 border border-white/10 rounded-lg p-3 font-sans leading-relaxed max-h-40 overflow-y-auto"
            data-testid={`asset-caption-${asset.id}`}
          >
            {asset.caption}
          </pre>
        </div>

        {/* Share row */}
        <div>
          <div className="overline text-white/40 mb-2">Share</div>
          <div className="flex items-center gap-2 flex-wrap">
            <ShareBtn
              platform="linkedin"
              label="LinkedIn"
              icon={<Linkedin className="w-3.5 h-3.5" />}
              onClick={() => share("linkedin")}
              dataTestid={`asset-share-linkedin-${asset.id}`}
            />
            <ShareBtn
              platform="x"
              label="X"
              icon={<Twitter className="w-3.5 h-3.5" />}
              onClick={() => share("x")}
              dataTestid={`asset-share-x-${asset.id}`}
            />
            <ShareBtn
              platform="facebook"
              label="Facebook"
              icon={<Facebook className="w-3.5 h-3.5" />}
              onClick={() => share("facebook")}
              dataTestid={`asset-share-facebook-${asset.id}`}
            />
            <button
              type="button"
              onClick={copyVideoUrl}
              data-testid={`asset-share-copyurl-${asset.id}`}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition-colors"
              title="Copy direct video URL — paste into Instagram/TikTok composer after download"
            >
              {urlCopied ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" /> URL copied
                </>
              ) : (
                <>
                  <LinkIcon className="w-3.5 h-3.5" /> Copy URL
                </>
              )}
            </button>
            <button
              type="button"
              onClick={copyEmbed}
              data-testid={`asset-share-embed-${asset.id}`}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-400/40 text-white/70 hover:text-emerald-200 transition-colors"
              title={
                asset.withAudio
                  ? "Copy HTML <video> tag with controls (narrated — sound-on)"
                  : "Copy HTML <video> tag with autoplay-muted-loop (drop into Notion, Webflow, email signatures)"
              }
            >
              {embedCopied ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Embed copied
                </>
              ) : (
                <>
                  <Code2 className="w-3.5 h-3.5" /> Copy embed
                </>
              )}
            </button>
          </div>
          <p className="text-[11px] text-white/30 mt-2 leading-snug">
            LinkedIn &amp; Facebook unfurl the kit page · X sometimes auto-embeds the MP4 · For Instagram/TikTok, download then upload natively · Embed snippet works in Notion, Webflow, email signatures, help docs.
          </p>
        </div>

        {/* Download */}
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="text-xs text-white/40 font-mono">{asset.size}</div>
          <a
            href={asset.video}
            download={asset.filename}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-bold transition-colors"
            data-testid={`asset-download-${asset.id}`}
          >
            <Download className="w-4 h-4" /> Download
          </a>
        </div>
      </div>
    </article>
  );
}
